import { FC, ReactNode, memo } from "react";
import { makeBreakpointClasses } from "apps/website/utils/classes";
import * as FlexMap from "apps/website/maps/Flex.map";
import { breakpointFlexClasses } from "apps/website/utils/classes/flex";
import { WithTestID } from "apps/website/types";

import * as ColumnMap from "./Column.map";

/**
 * Column component interface
 *
 * The Column component defines grid columns with responsive spans, alignment,
 * and positioning within the 12-column grid system.
 */
export interface IColumn extends WithTestID {
  /** HTML tag to render (div, li, article, nav) */
  tag?: ColumnMap.AllowedTag;
  /** Number of columns to span (1-12) or responsive breakpoint object */
  spans?: ColumnMap.Span | ColumnMap.BreakpointSpan;
  /** Column offset for positioning (1-13) or responsive breakpoint object */
  offset?: ColumnMap.Offset | ColumnMap.BreakpointOffset;
  /** Flexbox justify content alignment */
  justify?: FlexMap.Justify | FlexMap.BreakpointJustify;
  /** Flexbox align items alignment */
  align?: FlexMap.Align | FlexMap.BreakpointAlign;
  /** Flexbox direction (row, column, etc.) */
  direction?: FlexMap.Direction | FlexMap.BreakpointDirection;
  /** Unique identifier for the column element */
  id?: string;
  /** Accessible label for screen readers */
  label?: string;
  /** Component identifier for debugging and testing */
  component?: string;
  /** Content to be rendered inside the column */
  children: ReactNode;
  /** Additional CSS classes applied to the column */
  className?: string;
  /** Click handler for interactive columns */
  onClick?: () => void;
  /** Linking identifier for tracking and analytics */
  "data-linkingid"?: string;
}

/**
 * Column component
 *
 * A flexible column component that defines grid columns with responsive spans,
 * alignment, and positioning within the 12-column grid system.
 *
 * @example
 * ```tsx
 * <Column spans={12} md={6} lg={4}>
 *   <div>Responsive column content</div>
 * </Column>
 * ```
 *
 * @example
 * ```tsx
 * <Column
 *   spans={{ default: 12, md: 6, lg: 4 }}
 *   justify="center"
 *   align="center"
 * >
 *   <div>Centered column content</div>
 * </Column>
 * ```
 */
const Column: FC<IColumn> = ({
  "data-testid": testId,
  "data-linkingid": linkingId,
  tag = "div",
  spans = 12,
  justify = "default",
  align = "default",
  offset = "default",
  direction = "column",
  id = undefined,
  label = undefined,
  component = "Column",
  children,
  className = "",
  onClick,
}) => {
  const Tag = tag;

  const computedClasses = makeBreakpointClasses([
    [ spans, ColumnMap.defaultSpansClassMap, ColumnMap.collectiveSpansClassMap ],
    [ offset, ColumnMap.defaultOffsetClassMap, ColumnMap.collectiveOffsetClassMap ],
    ...breakpointFlexClasses(align, justify, direction),
  ], className);

  return (
    <Tag data-component={component} data-testid={testId} data-linkingid={linkingId} id={id} aria-label={label} className={`flex box-border ${computedClasses}`} onClick={onClick}>
      { children }
    </Tag>
  );
};

export default memo(Column);
